import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  selectTool,
  currentTool,
  subscribeToolChange,
  subscribeChartWidget,
  isChartReady,
  getToggle,
  toggle,
} from "./chartToolBridge";

/** The library ships its icons as SVG markup strings; they are static assets
 *  from the bundle, not user input. */
function Icon({ svg }) {
  return (
    <span
      className="fx-chart-rail__icon"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

/**
 * MT5 Lines flyout glyphs (match MT5 Web / mobile rail icons).
 * Thin stroke + hollow nodes (#fff fill) — not TradingView solid icons.
 */
const MT5_LINE = {
  trendLine:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><line x1="4.8" y1="19.2" x2="19.2" y2="4.8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.5"><circle cx="4.8" cy="19.2" r="2.15"/><circle cx="19.2" cy="4.8" r="2.15"/></g></svg>',
  horzLine:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><line x1="3.2" y1="12" x2="20.8" y2="12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle class="mt5-node" cx="12" cy="12" r="2.15" fill="#fff" stroke="currentColor" stroke-width="1.5"/></svg>',
  horzRay:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><line x1="4.6" y1="12" x2="20.8" y2="12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle class="mt5-node" cx="4.6" cy="12" r="2.15" fill="#fff" stroke="currentColor" stroke-width="1.5"/></svg>',
  vertLine:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><line x1="12" y1="3.2" x2="12" y2="20.8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle class="mt5-node" cx="12" cy="12" r="2.15" fill="#fff" stroke="currentColor" stroke-width="1.5"/></svg>',
  infoLine:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><line x1="3.8" y1="18.8" x2="17.6" y2="5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.5"><circle cx="3.8" cy="18.8" r="2.05"/><circle cx="17.6" cy="5" r="2.05"/></g><rect x="14.2" y="14.4" width="6.2" height="6.2" rx="0.7" fill="#fff" stroke="currentColor" stroke-width="1.35"/></svg>',
  ruler:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><line x1="3.8" y1="18.8" x2="17.6" y2="5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.5"><circle cx="3.8" cy="18.8" r="2.05"/><circle cx="17.6" cy="5" r="2.05"/></g><rect x="12.6" y="14.6" width="8.6" height="5.6" rx="0.6" fill="#fff" stroke="currentColor" stroke-width="1.3"/><text x="16.9" y="18.7" text-anchor="middle" font-size="4.6" font-family="Tahoma,Arial,sans-serif" font-weight="700" fill="currentColor" stroke="none">E</text></svg>',
  /* MT5: start circle → line → arrowhead → end circle */
  arrowLine:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><line x1="4.6" y1="19.2" x2="14.8" y2="9.2" stroke="currentColor" stroke-width="1.55" stroke-linecap="round"/><path d="M14.2 11.35 L18.35 7.2 L15.05 6.55 Z" fill="currentColor" stroke="none"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.45"><circle cx="4.6" cy="19.2" r="2.05"/><circle cx="19.5" cy="4.7" r="2.05"/></g></svg>',
  /* MT5 Ray: single hollow node at the start only */
  ray:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><line x1="4.8" y1="19" x2="20.5" y2="4.2" stroke="currentColor" stroke-width="1.55" stroke-linecap="round"/><circle class="mt5-node" cx="4.8" cy="19" r="2.1" fill="#fff" stroke="currentColor" stroke-width="1.45"/></svg>',
  /* MT5 Extended Line: line past ends + only 2 hollow nodes */
  extended:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><line x1="2.4" y1="20.8" x2="21.6" y2="3.2" stroke="currentColor" stroke-width="1.55" stroke-linecap="round"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.45"><circle cx="7.4" cy="16.2" r="2"/><circle cx="16.6" cy="7.8" r="2"/></g></svg>',
  crossLine:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><line x1="3.2" y1="12" x2="20.8" y2="12" stroke="currentColor" stroke-width="1.55" stroke-linecap="round"/><line x1="12" y1="3.2" x2="12" y2="20.8" stroke="currentColor" stroke-width="1.55" stroke-linecap="round"/><circle class="mt5-node" cx="12" cy="12" r="2.15" fill="#fff" stroke="currentColor" stroke-width="1.5"/></svg>',
  /* Arc meets both the base and the angled line */
  trendAngle:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><line x1="5" y1="19" x2="20.4" y2="19" stroke="currentColor" stroke-width="1.55" stroke-linecap="round"/><line x1="5" y1="19" x2="18.8" y2="5.2" stroke="currentColor" stroke-width="1.55" stroke-linecap="round"/><path d="M10.35 19A6.15 6.15 0 0 0 14.55 13.55" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="butt"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.45"><circle cx="5" cy="19" r="2"/><circle cx="18.8" cy="5.2" r="2"/></g></svg>',
  /* MT5: two parallel diagonals, hollow node at every endpoint */
  disjoint:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><line x1="3.6" y1="14.8" x2="15.4" y2="4.6" stroke="currentColor" stroke-width="1.55" stroke-linecap="round"/><line x1="8.6" y1="19.6" x2="20.4" y2="9.4" stroke="currentColor" stroke-width="1.55" stroke-linecap="round"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.45"><circle cx="3.6" cy="14.8" r="2"/><circle cx="15.4" cy="4.6" r="2"/><circle cx="8.6" cy="19.6" r="2"/><circle cx="20.4" cy="9.4" r="2"/></g></svg>',
  /* Diagonal + horizontal with a clear gap between the two lines */
  flatBottom:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><line x1="4.2" y1="12.4" x2="16.6" y2="4.6" stroke="currentColor" stroke-width="1.55" stroke-linecap="round"/><line x1="4.2" y1="19.2" x2="20" y2="19.2" stroke="currentColor" stroke-width="1.55" stroke-linecap="round"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.45"><circle cx="4.2" cy="12.4" r="2"/><circle cx="16.6" cy="4.6" r="2"/><circle cx="4.2" cy="19.2" r="2"/><circle cx="20" cy="19.2" r="2"/></g></svg>',
};

/** MT5 Line Studies / mobile chart-rail glyphs. Stroke-based, 24px, dark. */
const RAIL = {
  cursor:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><g fill="currentColor"><rect x="10.85" y="1.5" width="2.3" height="6.15" rx="1.15"/><rect x="10.85" y="16.35" width="2.3" height="6.15" rx="1.15"/><rect x="1.5" y="10.85" width="6.15" height="2.3" rx="1.15"/><rect x="16.35" y="10.85" width="6.15" height="2.3" rx="1.15"/></g><circle class="mt5-node" cx="12" cy="12" r="3.55" fill="#fff" stroke="currentColor" stroke-width="1.9"/><circle cx="12" cy="12" r="1.35" fill="currentColor"/></svg>',
  trendLine: MT5_LINE.trendLine,
  parallel:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><g stroke="currentColor" stroke-width="2.05" stroke-linecap="round"><line x1="5.2" y1="18.6" x2="18.8" y2="5"/><line x1="11.4" y1="20.2" x2="19.6" y2="12"/></g><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.75"><circle cx="5.2" cy="18.6" r="2.55"/><circle cx="18.8" cy="5" r="2.55"/></g></svg>',
  rectangle:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><rect x="5.1" y="5.1" width="13.8" height="13.8" rx="2.6" fill="none" stroke="currentColor" stroke-width="1.85"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.7"><circle cx="5.1" cy="5.1" r="2.35"/><circle cx="18.9" cy="5.1" r="2.35"/><circle cx="5.1" cy="18.9" r="2.35"/><circle cx="18.9" cy="18.9" r="2.35"/></g></svg>',
  gann:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><line x1="5.05" y1="18.95" x2="18.95" y2="5.05" stroke="currentColor" stroke-width="2.2"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="2"><circle cx="5.05" cy="18.95" r="2.7"/><circle cx="12" cy="12" r="2.7"/><circle cx="18.95" cy="5.05" r="2.7"/></g></svg>',
  fib:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><g stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="2.6" y1="5.6" x2="21.4" y2="5.6"/><line x1="2.6" y1="12" x2="21.4" y2="12"/><line x1="2.6" y1="18.4" x2="21.4" y2="18.4"/></g><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.85"><circle cx="16.6" cy="5.6" r="2.85"/><circle cx="16.6" cy="12" r="2.85"/><circle cx="7.1" cy="18.4" r="2.85"/></g></svg>',
  text:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M3.8 3.6h16.4v2.55H13.4V20.4h-2.8V6.15H3.8z"/></svg>',
  hide:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M2.2 12A12 12 0 0 1 21.8 12A12 12 0 0 1 2.2 12Z" fill="none" stroke="currentColor" stroke-width="1.95" stroke-linejoin="round"/><circle class="mt5-node" cx="12" cy="12" r="3.4" fill="#fff" stroke="currentColor" stroke-width="1.95"/><circle cx="12" cy="12" r="1.5" fill="currentColor"/></svg>',
  hideActive:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M2.2 12A12 12 0 0 1 21.8 12A12 12 0 0 1 2.2 12Z" fill="none" stroke="currentColor" stroke-width="1.95" stroke-linejoin="round"/><circle class="mt5-node" cx="12" cy="12" r="3.4" fill="#fff" stroke="currentColor" stroke-width="1.95"/><circle cx="12" cy="12" r="1.5" fill="currentColor"/><path d="M4.7 19.3L19.3 4.7" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>',
  lock:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><rect x="5.3" y="10.4" width="13.4" height="10.3" rx="2.1" fill="none" stroke="currentColor" stroke-width="1.9"/><path d="M8.2 10.4V7.4a3.8 3.8 0 0 1 7.5-.15" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/><rect x="11.15" y="14.15" width="1.7" height="2.55" rx=".7" fill="currentColor"/></svg>',
  lockActive:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><rect x="5.3" y="10.4" width="13.4" height="10.3" rx="2.1" fill="none" stroke="currentColor" stroke-width="1.9"/><path d="M8.2 10.4V7.5a3.8 3.8 0 0 1 7.6 0v2.9" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/><rect x="11.15" y="14.15" width="1.7" height="2.55" rx=".7" fill="currentColor"/></svg>',
  objectTree:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><circle class="mt5-node" cx="6.6" cy="4.7" r="2.45" fill="#fff" stroke="currentColor" stroke-width="1.85"/><path d="M6.6 7.15v11.5M6.6 11.15h7.1M6.6 18.65h7.1" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.85"><rect x="13.7" y="8.55" width="5.5" height="5.2" rx="1.15"/><rect x="13.7" y="16.05" width="5.5" height="5.2" rx="1.15"/></g></svg>',
  tradePanel:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><g fill="currentColor"><rect x="3.1" y="3.9" width="2.15" height="2.15" rx=".35"/><rect x="7.1" y="4.15" width="13.8" height="1.7" rx=".45"/><rect x="3.1" y="8.55" width="2.15" height="2.15" rx=".35"/><rect x="7.1" y="8.8" width="11.6" height="1.7" rx=".45"/><rect x="3.1" y="13.2" width="2.15" height="2.15" rx=".35"/><rect x="7.1" y="13.45" width="9.4" height="1.7" rx=".45"/><rect x="3.1" y="17.85" width="2.15" height="2.15" rx=".35"/><rect x="7.1" y="18.1" width="7.2" height="1.7" rx=".45"/></g></svg>',
  historyClock:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M4.55 9.35A8.1 8.1 0 1 0 8.15 4.6" fill="none" stroke="currentColor" stroke-width="2.05" stroke-linecap="butt"/><path fill="currentColor" d="M8.2 4.55L4.55 3.15l.85 3.7z"/><path d="M12 7.05V12h5.05" fill="none" stroke="currentColor" stroke-width="2.05" stroke-linecap="round" stroke-linejoin="round"/></svg>',
};

const SVG = {
  parallel: RAIL.parallel,
  objectTree:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="28" height="28"><g fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M8 6v10M8 12h6M14 12v6"/><circle cx="8" cy="6" r="2" fill="currentColor" stroke="none"/><circle cx="14" cy="12" r="2" fill="currentColor" stroke="none"/><circle cx="14" cy="20" r="2" fill="currentColor" stroke="none"/><circle cx="20" cy="12" r="2" fill="currentColor" stroke="none"/></g></svg>',
  ray:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="28" height="28"><g fill="currentColor"><path d="M6.35 20.35l12-12-.7-.7-12 12z"/><circle cx="6" cy="20" r="1.6"/><path d="M17.2 7.2l5.3-1.2-1.2 5.3-.9-.9 1.4-1.4-1.4 1.4z"/></g></svg>',
  extended:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="28" height="28"><g fill="currentColor"><path d="M3.35 22.35l21-16-.7-.7-21 16z"/><circle cx="10" cy="17" r="1.4"/><circle cx="18" cy="11" r="1.4"/></g></svg>',
  vertical:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="28" height="28"><g fill="currentColor"><path d="M13.5 4h1v20h-1z"/><circle cx="14" cy="9" r="1.5"/><circle cx="14" cy="19" r="1.5"/></g></svg>',
  horizontalRay:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="28" height="28"><g fill="currentColor"><path d="M6 14.5h16v-1H6z"/><circle cx="8" cy="14" r="1.5"/><path d="M20 11.5l4 2.5-4 2.5v-5z"/></g></svg>',
  crossLine:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="28" height="28"><g fill="currentColor"><path d="M4 14.5h20v-1H4z"/><path d="M13.5 4h1v20h-1z"/></g></svg>',
  trendAngle:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="28" height="28"><g fill="currentColor"><path d="M6 20h16v-1H7.7l10.6-10.6-.7-.7L6 19.3V20z"/><path d="M9 20a7 7 0 0 1 5-6.7v1.1A6 6 0 0 0 10 20H9z"/></g></svg>',
  disjoint:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="28" height="28"><g fill="currentColor"><path d="M5.35 18.35l10-8-.7-.7-10 8z"/><path d="M12.35 22.35l10-8-.7-.7-10 8z"/><circle cx="5" cy="18" r="1.4"/><circle cx="15" cy="10" r="1.4"/><circle cx="12" cy="22" r="1.4"/><circle cx="22" cy="14" r="1.4"/></g></svg>',
  flatBottom:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="28" height="28"><g fill="currentColor"><path d="M5 20h18v-1H5z"/><path d="M6.35 18.35l8-10-.7-.7-8 10z"/><circle cx="6" cy="18" r="1.4"/><circle cx="14" cy="8" r="1.4"/><circle cx="20" cy="19.5" r="1.4"/></g></svg>',
  arrowLine:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="28" height="28"><g fill="currentColor"><path d="M6.35 20.35l11-11-.7-.7-11 11z"/><path d="M16 7l6 1-3.2 3.2-.9-.9L20 8.7 16 8V7z"/><circle cx="6" cy="20" r="1.4"/></g></svg>',
  regression:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><g stroke="currentColor" stroke-width="2.05" stroke-linecap="round"><line x1="7.6" y1="10.2" x2="16.6" y2="1.2"/><line x1="5.2" y1="18.6" x2="18.8" y2="5"/><line x1="7.4" y1="22.8" x2="16.4" y2="13.8"/></g><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.75"><circle cx="5.2" cy="18.6" r="2.55"/><circle cx="18.8" cy="5" r="2.55"/></g></svg>',
  pitchfork:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><g stroke="currentColor" stroke-width="2.15" stroke-linecap="round"><line x1="4.6" y1="5.8" x2="16.4" y2="18.6"/><line x1="5" y1="18.4" x2="20.4" y2="5.6"/><line x1="6.8" y1="8.2" x2="13.4" y2="2.8"/><line x1="14" y1="16.4" x2="19.4" y2="11.8"/></g><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.9"><circle cx="6.8" cy="8.2" r="2.5"/><circle cx="6.8" cy="16.8" r="2.5"/><circle cx="14" cy="16.4" r="2.5"/></g></svg>',
  stdevChannel:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><g stroke="currentColor" stroke-width="2.05" stroke-linecap="round"><line x1="4.6" y1="12.8" x2="13.8" y2="3.6"/><line x1="7.6" y1="16.4" x2="16.8" y2="7.2"/><line x1="10.6" y1="20" x2="19.8" y2="10.8"/><line x1="12.4" y1="6.8" x2="15.4" y2="6.8"/><line x1="13.9" y1="5.3" x2="13.9" y2="8.3"/><line x1="9.4" y1="16.4" x2="12.4" y2="16.4"/><line x1="10.9" y1="14.9" x2="10.9" y2="17.9"/></g></svg>',
  gannLine:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><line x1="5.2" y1="18.8" x2="18.8" y2="5.2" stroke="currentColor" stroke-width="2.15" stroke-linecap="round"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.8"><circle cx="5.2" cy="18.8" r="2.4"/><circle cx="18.8" cy="5.2" r="2.4"/></g></svg>',
  gannFan:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><g stroke="currentColor" stroke-width="1.9" stroke-linecap="round" fill="none"><line x1="5.2" y1="18.8" x2="19" y2="4.8"/><line x1="5.2" y1="18.8" x2="19.2" y2="10.2"/><line x1="5.2" y1="18.8" x2="19.2" y2="15.4"/><line x1="5.2" y1="18.8" x2="14.4" y2="19"/></g><circle class="mt5-node" cx="5.2" cy="18.8" r="2.3" fill="#fff" stroke="currentColor" stroke-width="1.75"/></svg>',
  gannGrid:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><g stroke="currentColor" stroke-width="1.7" fill="none"><rect x="4.4" y="4.4" width="15.2" height="15.2" rx="0.6"/><path d="M4.4 9.5h15.2M4.4 14.5h15.2M9.5 4.4v15.2M14.5 4.4v15.2"/></g></svg>',
  fibTime:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><g stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="5" y1="4.2" x2="5" y2="19.8"/><line x1="10" y1="4.2" x2="10" y2="19.8"/><line x1="16.5" y1="4.2" x2="16.5" y2="19.8"/></g></svg>',
  fibFan:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><g stroke="currentColor" stroke-width="1.9" stroke-linecap="round" fill="none"><line x1="4.8" y1="19.2" x2="19.2" y2="4.8"/><line x1="4.8" y1="19.2" x2="19.2" y2="11"/><line x1="4.8" y1="19.2" x2="19.2" y2="16.6"/></g></svg>',
  fibArcs:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><g fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round"><path d="M5 19a8.2 8.2 0 0 1 8.2-8.2"/><path d="M5 19a11.2 11.2 0 0 1 11.2-11.2"/><path d="M5 19a14 14 0 0 1 14-14"/></g></svg>',
  fibExpansion:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><line x1="5.05" y1="18.95" x2="18.95" y2="5.05" stroke="currentColor" stroke-width="2.1"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.9"><circle cx="5.05" cy="18.95" r="2.45"/><circle cx="12" cy="12" r="2.45"/><circle cx="18.95" cy="5.05" r="2.45"/></g></svg>',
  fibChannel:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><g stroke="currentColor" stroke-width="1.85" stroke-linecap="round" fill="none"><line x1="4.4" y1="16.2" x2="16.2" y2="4.4"/><line x1="7.2" y1="19.2" x2="19.2" y2="7.2"/><line x1="10.4" y1="20.4" x2="20.4" y2="10.4"/></g></svg>',
  circle:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><circle cx="11.4" cy="13" r="7.6" fill="none" stroke="currentColor" stroke-width="1.85"/><line x1="11.4" y1="13" x2="16.7" y2="6.4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><circle cx="11.4" cy="13" r="1.15" fill="currentColor"/><circle class="mt5-node" cx="16.7" cy="6.4" r="2.25" fill="#fff" stroke="currentColor" stroke-width="1.7"/></svg>',
  ellipse:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><ellipse cx="12" cy="12" rx="8.6" ry="5.15" fill="none" stroke="currentColor" stroke-width="1.85"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.7"><circle cx="3.4" cy="12" r="2.2"/><circle cx="20.6" cy="12" r="2.2"/><circle cx="12" cy="6.85" r="2.2"/><circle cx="12" cy="17.15" r="2.2"/></g></svg>',
  triangle:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M12 4.8L20.4 19.4H3.6z" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linejoin="round"/><g class="mt5-node" fill="#fff" stroke="currentColor" stroke-width="1.7"><circle cx="12" cy="4.8" r="2.3"/><circle cx="20.4" cy="19.4" r="2.3"/><circle cx="3.6" cy="19.4" r="2.3"/></g></svg>',
};
/**
 * MT5 Lines flyout — tools mapped to TradingView SupportedLineTools.
 */
const LINES_GROUP = {
  id: "lines",
  title: "Lines",
  defaultTool: "trend_line",
  icon: MT5_LINE.trendLine,
  tools: [
    { tool: "trend_line", title: "Trendline", svg: MT5_LINE.trendLine, shortcut: "Alt + L" },
    { tool: "horizontal_line", title: "Horizontal Line", svg: MT5_LINE.horzLine, shortcut: "Alt + H" },
    { tool: "horizontal_ray", title: "Horizontal Ray", svg: MT5_LINE.horzRay },
    { tool: "vertical_line", title: "Vertical Line", svg: MT5_LINE.vertLine, shortcut: "Alt + V" },
    { tool: "info_line", title: "Info Line", svg: MT5_LINE.infoLine, shortcut: "Alt + I" },
    { tool: "measure", title: "Ruler", svg: MT5_LINE.ruler },
    { tool: "arrow", title: "Arrowed Line", svg: MT5_LINE.arrowLine, shortcut: "Alt + A" },
    { tool: "ray", title: "Ray", svg: MT5_LINE.ray, shortcut: "Alt + R" },
    { tool: "extended", title: "Extended Line", svg: MT5_LINE.extended, shortcut: "Alt + E" },
    { tool: "cross_line", title: "Cross Line", svg: MT5_LINE.crossLine, shortcut: "Alt + C" },
    { tool: "trend_angle", title: "Trend By Angle", svg: MT5_LINE.trendAngle },
    { tool: "disjoint_angle", title: "Disjoint Channel", svg: MT5_LINE.disjoint },
    { tool: "flat_bottom", title: "Flat Top/Bottom", svg: MT5_LINE.flatBottom },
  ],
};

/** MT5 Lines Alt+key → tool id (same as shortcuts shown in the flyout). */
const LINE_ALT_HOTKEYS = {
  l: "trend_line",
  h: "horizontal_line",
  v: "vertical_line",
  i: "info_line",
  a: "arrow",
  r: "ray",
  e: "extended",
  c: "cross_line",
};

/**
 * MT5 Channels flyout — TV equivalents (StdDev ≈ Fib Channel).
 */
const CHANNELS_GROUP = {
  id: "channels",
  title: "Channels",
  defaultTool: "regression_trend",
  icon: SVG.regression,
  tools: [
    { tool: "regression_trend", title: "Regression Channel", svg: SVG.regression },
    { tool: "parallel_channel", title: "Equidistant Channel", svg: RAIL.parallel },
    { tool: "fib_channel", title: "StdDev Channel", svg: SVG.stdevChannel },
    { tool: "pitchfork", title: "Andrews Pitchfork", svg: SVG.pitchfork },
  ],
};

/**
 * MT5 Gann flyout — sits above Shapes on the rail.
 */
const GANN_GROUP = {
  id: "gann",
  title: "Gann",
  defaultTool: "gannbox",
  icon: RAIL.gann,
  tools: [
    { tool: "gannbox", title: "Gann Line", svg: RAIL.gann },
    { tool: "gannbox_fan", title: "Gann Fan", svg: SVG.gannFan },
    { tool: "gannbox_square", title: "Gann Grid", svg: SVG.gannGrid },
  ],
};

/**
 * MT5 Shapes flyout.
 */
const SHAPES_GROUP = {
  id: "shapes",
  title: "Shapes",
  defaultTool: "rectangle",
  icon: RAIL.rectangle,
  tools: [
    { tool: "rectangle", title: "Rectangle", svg: RAIL.rectangle },
    { tool: "circle", title: "Circle", svg: SVG.circle },
    { tool: "triangle", title: "Triangle", svg: SVG.triangle },
    { tool: "ellipse", title: "Ellipse", svg: SVG.ellipse },
  ],
};

const TOOL_GROUPS = [LINES_GROUP, CHANNELS_GROUP, GANN_GROUP, SHAPES_GROUP];

const ALL_GROUP_TOOLS = new Set(TOOL_GROUPS.flatMap((g) => g.tools.map((t) => t.tool)));

function groupContainsTool(group, tool) {
  return group.tools.some((t) => t.tool === tool);
}

function iconForGroup(group, selectedByGroup) {
  const selected = selectedByGroup[group.id];
  const match = group.tools.find((t) => t.tool === selected);
  return match?.svg || group.icon;
}

export default function ChartToolRail({
  onOpenTradePanel,
  onOpenHistory,
  dockPanel = "trade",
  drawingsOpen = false,
  onToggleDrawings,
  isMobile = false,
} = {}) {
  const [active, setActive] = useState("cursor");
  const [hidden, setHidden] = useState(false);
  const [locked, setLocked] = useState(false);
  const [flyoutId, setFlyoutId] = useState(null);
  const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });
  const [selectedByGroup, setSelectedByGroup] = useState({
    lines: "trend_line",
    channels: "regression_trend",
    gann: "gannbox",
    shapes: "rectangle",
  });
  const flyoutRef = useRef(null);
  const groupBtnRefs = useRef({});

  useEffect(() => {
    let unsubTool = () => {};
    let poll = null;

    const attach = () => {
      if (!isChartReady()) return false;
      const tool = currentTool() || "cursor";
      setActive(tool);
      setHidden(getToggle("hide"));
      setLocked(getToggle("lock"));
      if (ALL_GROUP_TOOLS.has(tool)) {
        setSelectedByGroup((prev) => {
          const next = { ...prev };
          for (const g of TOOL_GROUPS) {
            if (groupContainsTool(g, tool)) next[g.id] = tool;
          }
          return next;
        });
      }
      unsubTool();
      unsubTool = subscribeToolChange(() => {
        const now = currentTool() || "cursor";
        setActive(now);
        if (ALL_GROUP_TOOLS.has(now)) {
          setSelectedByGroup((prev) => {
            const next = { ...prev };
            for (const g of TOOL_GROUPS) {
              if (groupContainsTool(g, now)) next[g.id] = now;
            }
            return next;
          });
        }
      });
      return true;
    };

    const unsubWidget = subscribeChartWidget((w) => {
      unsubTool();
      unsubTool = () => {};
      if (!w) return;
      if (!attach() && !poll) {
        poll = setInterval(() => {
          if (attach()) {
            clearInterval(poll);
            poll = null;
          }
        }, 300);
      }
    });

    return () => {
      if (poll) clearInterval(poll);
      unsubTool();
      unsubWidget();
    };
  }, []);

  useEffect(() => {
    if (!flyoutId) return undefined;
    const isInsideFlyoutUi = (target) => {
      if (!target || typeof target.closest !== "function") return false;
      if (flyoutRef.current?.contains(target)) return true;
      const btn = groupBtnRefs.current[flyoutId];
      if (btn?.contains(target)) return true;
      return false;
    };
    const close = () => setFlyoutId(null);
    const onDown = (e) => {
      if (isInsideFlyoutUi(e.target)) return;
      close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("blur", close);
    const iframeDocs = [];
    document.querySelectorAll("iframe").forEach((frame) => {
      try {
        const doc = frame.contentDocument;
        if (!doc) return;
        doc.addEventListener("pointerdown", close, true);
        iframeDocs.push(doc);
      } catch {
        /* cross-origin chart iframe still covered by the dismiss overlay */
      }
    });
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", close);
      iframeDocs.forEach((doc) => doc.removeEventListener("pointerdown", close, true));
    };
  }, [flyoutId]);

  const placeFlyout = useCallback((groupId) => {
    const btn = groupBtnRefs.current[groupId];
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const pad = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Mobile: compact rows; width is content-sized in CSS (Channels narrower than Lines).
    const group = TOOL_GROUPS.find((g) => g.id === groupId);
    const count = group?.tools.length || 8;
    const rowH = isMobile ? 34 : 44;
    const naturalH = count * rowH + 8;
    const maxH = Math.max(160, vh - pad * 2);
    const menuH = Math.min(naturalH, maxH);
    // Estimate width for clamping only; actual width = max-content on mobile.
    const menuW = isMobile
      ? Math.min(200, vw - pad * 2 - 36)
      : 260;
    let left = r.right + 6;
    if (left + menuW > vw - pad) left = Math.max(pad, r.left - menuW - 6);
    if (left < pad) left = pad;
    let top = r.top;
    if (top + menuH > vh - pad) top = Math.max(pad, vh - menuH - pad);
    if (top < pad) top = pad;
    const col = btn.closest(".fx-chart-tools-col");
    const dismissLeft = Math.round(col?.getBoundingClientRect().right ?? r.right);
    setFlyoutPos({
      top: Math.round(top),
      left: Math.round(left),
      maxHeight: Math.round(menuH),
      dismissLeft,
    });
  }, [isMobile]);

  const pick = useCallback(async (tool) => {
    setActive(tool);
    setFlyoutId(null);
    await selectTool(tool);
  }, []);

  const openGroup = useCallback(
    (groupId) => {
      setFlyoutId((prev) => {
        if (prev === groupId) return null;
        requestAnimationFrame(() => placeFlyout(groupId));
        return groupId;
      });
    },
    [placeFlyout]
  );

  const pickGroupTool = useCallback(
    async (groupId, tool) => {
      setSelectedByGroup((prev) => ({ ...prev, [groupId]: tool }));
      setActive(tool);
      setFlyoutId(null);
      await selectTool(tool);
    },
    []
  );

  // MT5: Alt+L / Alt+H / Alt+V … select the matching Lines tool directly.
  useEffect(() => {
    const onHotkey = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      const tool = LINE_ALT_HOTKEYS[String(e.key).toLowerCase()];
      if (!tool) return;
      e.preventDefault();
      pickGroupTool("lines", tool);
    };
    document.addEventListener("keydown", onHotkey);
    return () => document.removeEventListener("keydown", onHotkey);
  }, [pickGroupTool]);

  const openFlyoutGroup = useMemo(
    () => TOOL_GROUPS.find((g) => g.id === flyoutId) || null,
    [flyoutId]
  );

  const isDark =
    typeof document !== "undefined" &&
    !!document.querySelector(".trading-platform.dark");

  return (
      <div className="fx-chart-rail fx-chart-rail--mt5" role="toolbar" aria-label="Chart drawing tools">
        <div className="fx-chart-rail__group">
          <button
            type="button"
            className={`fx-chart-rail__btn${active === "cursor" ? " is-active" : ""}`}
            title="Crosshair"
            aria-label="Crosshair"
            aria-pressed={active === "cursor"}
            onClick={() => pick("cursor")}
          >
            <Icon svg={RAIL.cursor} />
          </button>

          {TOOL_GROUPS.map((group) => {
            const groupActive = groupContainsTool(group, active);
            return (
              <button
                key={group.id}
                ref={(el) => {
                  groupBtnRefs.current[group.id] = el;
                }}
                type="button"
                className={`fx-chart-rail__btn fx-chart-rail__btn--has-menu${groupActive ? " is-active" : ""}${flyoutId === group.id ? " is-open" : ""}`}
                title={group.title}
                aria-label={group.title}
                aria-pressed={groupActive}
                aria-haspopup="menu"
                aria-expanded={flyoutId === group.id}
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  const onArrow = r.right - e.clientX <= 18 && r.bottom - e.clientY <= 18;
                  if (onArrow) {
                    openGroup(group.id);
                    return;
                  }
                  const tool = selectedByGroup[group.id] || group.defaultTool;
                  pickGroupTool(group.id, tool);
                }}
              >
                <Icon svg={iconForGroup(group, selectedByGroup)} />
                {/* MT5: corner arrow opens options; the icon itself applies the tool */}
                <span className="fx-chart-rail__menu-mark" aria-hidden />
              </button>
            );
          })}

          <button
            type="button"
            className={`fx-chart-rail__btn${active === "fib_retracement" ? " is-active" : ""}`}
            title="Fibonacci retracement"
            aria-label="Fibonacci retracement"
            aria-pressed={active === "fib_retracement"}
            onClick={() => pick("fib_retracement")}
          >
            <Icon svg={RAIL.fib} />
          </button>

          <button
            type="button"
            className={`fx-chart-rail__btn${active === "text" ? " is-active" : ""}`}
            title="Text"
            aria-label="Text"
            aria-pressed={active === "text"}
            onClick={() => pick("text")}
          >
            <Icon svg={RAIL.text} />
          </button>

          <button
            type="button"
            className={`fx-chart-rail__btn${hidden ? " is-active" : ""}`}
            title="Visibility"
            aria-label="Visibility"
            aria-pressed={hidden}
            onClick={() => setHidden(toggle("hide"))}
          >
            <Icon svg={hidden ? RAIL.hideActive : RAIL.hide} />
          </button>

          <button
            type="button"
            className={`fx-chart-rail__btn${locked ? " is-active" : ""}`}
            title="Lock"
            aria-label="Lock"
            aria-pressed={locked}
            onClick={() => setLocked(toggle("lock"))}
          >
            <Icon svg={locked ? RAIL.lockActive : RAIL.lock} />
          </button>

          <button
            type="button"
            className={`fx-chart-rail__btn${drawingsOpen ? " is-active" : ""}`}
            title="Manage Drawings"
            aria-label="Manage Drawings"
            aria-pressed={drawingsOpen}
            onClick={() => {
              setFlyoutId(null);
              onToggleDrawings?.();
            }}
          >
            <Icon svg={RAIL.objectTree} />
          </button>

          {!isMobile && (
            <>
              <button
                type="button"
                className={`fx-chart-rail__btn${dockPanel === "trade" ? " is-active" : ""}`}
                title="Positions"
                aria-label="Positions"
                aria-pressed={dockPanel === "trade"}
                onClick={() => {
                  setFlyoutId(null);
                  onOpenTradePanel?.();
                }}
              >
                <Icon svg={RAIL.tradePanel} />
              </button>
              <button
                type="button"
                className={`fx-chart-rail__btn${dockPanel === "history" ? " is-active" : ""}`}
                title="History"
                aria-label="History"
                aria-pressed={dockPanel === "history"}
                onClick={() => {
                  setFlyoutId(null);
                  onOpenHistory?.("deals");
                }}
              >
                <Icon svg={RAIL.historyClock} />
              </button>
            </>
          )}
        </div>

        {openFlyoutGroup &&
          typeof document !== "undefined" &&
          createPortal(
            <>
            <div
              className="mt5-rail-flyout-dismiss"
              aria-hidden
              style={{ left: flyoutPos.dismissLeft || 0 }}
              onPointerDown={(e) => {
                e.preventDefault();
                setFlyoutId(null);
              }}
            />
            <ul
              ref={flyoutRef}
              className={`mt5-rail-flyout${isMobile ? " mt5-rail-flyout--mobile" : ""}${isDark ? " mt5-rail-flyout--dark" : ""}`}
              role="menu"
              aria-label={openFlyoutGroup.title}
              style={{
                top: flyoutPos.top,
                left: flyoutPos.left,
                maxHeight: flyoutPos.maxHeight || undefined,
              }}
            >
              {openFlyoutGroup.tools.map((item) => {
                const selected = selectedByGroup[openFlyoutGroup.id] === item.tool;
                return (
                  <li key={item.tool} role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className={`mt5-rail-flyout__item${selected ? " is-active" : ""}`}
                      onClick={() => pickGroupTool(openFlyoutGroup.id, item.tool)}
                    >
                      <span className="mt5-rail-flyout__icon">
                        <Icon svg={item.svg} />
                      </span>
                      <span className="mt5-rail-flyout__label">{item.title}</span>
                      {item.shortcut ? (
                        <span className="mt5-rail-flyout__shortcut">{item.shortcut}</span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
            </>,
            document.body
          )}
      </div>
    );
}
